import React, { useCallback, useMemo } from 'react';
import { useMount } from 'react-use';

import { trackEvent } from '@surfline/web-common';
import ReelPlayer from 'components/ReelPlayer/ReelPlayer';
import { SL_WEB_FREEMIUM_CAM_HIGHLIGHTS } from 'common/treatments';
import { getSpotReportAssociated } from 'selectors/spot';
import { useAppSelector } from 'stores/hooks';
import { Camera } from 'types/camera';
import { SpotDetails } from 'types/spot';
import { RewindPlaylists } from 'utils/rewinds/playlistBlocks.types';
import { useTreatments } from 'utils/treatments';

import transformHighlights from './utils/transformHighlights';

const CamHighlightsPlayer = ({
  rewindsFetching,
  rewindFetchError,
  highlights,
  camera,
  spot,
  enabledFeatures = { wavePool: false },
}: {
  rewindsFetching?: boolean;
  rewindFetchError?: boolean;
  fetchDetails?: {
    camId: string;
    startDate: string;
    endDate: string;
  };
  highlights: RewindPlaylists | undefined;
  camera: Camera;
  spot: SpotDetails;
  enabledFeatures: {
    wavePool: boolean;
  };
}) => {
  const { utcOffset, timezone } = useAppSelector(getSpotReportAssociated);
  const treatments = useTreatments();
  const isPrerollTreatment = treatments?.[SL_WEB_FREEMIUM_CAM_HIGHLIGHTS] === 'preroll';

  // TODO: We are currently only showing this highlights player for free users (hence the harcoded value).
  // In the future we will need the permissions check because we plan to show a version slighly different for premium users.
  const hasPermissions = false;

  const reels = useMemo(
    () => transformHighlights(highlights, utcOffset, hasPermissions, timezone, isPrerollTreatment),
    [hasPermissions, highlights, utcOffset, timezone, isPrerollTreatment],
  );

  // Yesterday-only fallback keeps the rolling-unlock paywall and skips the pre-roll.
  const hasTodaySegments = useMemo(() => reels.some((reel) => reel.isFromToday), [reels]);
  const prerollEnabled = isPrerollTreatment && hasTodaySegments;

  const handleSegmentEvent = useCallback(
    (action: string, data?: { clipsPlayed: number; clipsAvailable: number }) => {
      trackEvent('Played Clip', {
        action,
        camId: camera?._id,
        camName: camera?.title,
        category: 'Cam Highlights Player',
        premiumCam: camera?.isPremium,
        smartCam: false,
        spotId: spot._id,
        spotName: spot.name,
        subregionId: spot.subregion?._id,
        clipsPlayed: data?.clipsPlayed,
        clipsAvailable: data?.clipsAvailable,
      });
    },
    [camera, spot],
  );

  useMount(() => {
    trackEvent('Played Webcam', {
      category: 'Cam Highlights Player',
      premiumCam: camera?.isPremium,
      spotName: spot.name,
      camName: camera?.title,
      camId: camera?._id,
      favorite: false,
      fullScreenEnabled: false,
      spotId: spot._id,
      subregionId: spot.subregion?._id,
      subregionName: spot.subregion?.name,
      pageScreenName: 'Spot Report',
      trigger: 'auto',
      videoType: 'Highlights',
    });
  });

  return (
    <ReelPlayer
      camera={camera}
      enableAds
      reels={reels}
      enableCornerControls
      spot={spot}
      segmentEvent={handleSegmentEvent}
      rewindsFetching={rewindsFetching}
      rewindFetchError={rewindFetchError}
      timezone={timezone}
      enabledFeatures={enabledFeatures}
      prerollEnabled={prerollEnabled}
    />
  );
};

export default CamHighlightsPlayer;
